import { DOCUMENT, registerLocaleData } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, from, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AvailableLanguagesEnum } from '../constants/available-languages.enum';
import { Messages } from 'app/models/entity-list.model';

@Injectable({ providedIn: 'root' })
export class MagicTranslateService {
  private readonly translateService = inject(TranslateService);
  private readonly document = inject(DOCUMENT);
  private readonly dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private readonly datepickerIntl = inject(MatDatepickerIntl);
  private readonly paginatorIntl = inject(MatPaginatorIntl);

  initLanguage(): Observable<any> {
    const storedLang = supportedLanguage(this.document.defaultView?.localStorage.getItem('Language'));
    const browserLang = supportedLanguage(this.translateService.getBrowserLang());
    const defaultLang = storedLang ?? browserLang ?? AvailableLanguagesEnum.EN;

    this.translateService.setFallbackLang(AvailableLanguagesEnum.EN);

    return this.changeUserLanguage(defaultLang);
  }

  updateUserLanguage(language: AvailableLanguagesEnum) {
    const localeId = supportedLanguage(language) ?? AvailableLanguagesEnum.EN;
    this.document.defaultView?.localStorage.setItem('Language', localeId);
    this.changeUserLanguage(localeId).subscribe();
  }

  changeUserLanguage(language: AvailableLanguagesEnum | string) {
    const localeId = supportedLanguage(language) ?? AvailableLanguagesEnum.EN;
    this.document.documentElement.lang = localeId;
    this.document.documentElement.dir = languageDirection(localeId);

    return this.translateService.use(localeId).pipe(
      switchMap(() => from(this.getLocale()[localeId]())),
      tap(module => {
        registerLocaleData(module.default);
        this.dateAdapter.setLocale(localeId);
        this.updateMaterialLabels();
      }),
    );
  }

  private getLocale() {
    return {
      [AvailableLanguagesEnum.FR]: () => import('@angular/common/locales/fr'),
      [AvailableLanguagesEnum.AR]: () => import('@angular/common/locales/ar'),
      [AvailableLanguagesEnum.ES]: () => import('@angular/common/locales/es'),
      [AvailableLanguagesEnum.IT]: () => import('@angular/common/locales/it'),
      [AvailableLanguagesEnum.EN]: () => import('@angular/common/locales/en'),
    };
  }

  getMessage(): Messages {
    return forkJoin({ emptyMessage: this.translateService.get('no-data'), totalMessage: this.translateService.get('total') });
  }

  private updateMaterialLabels(): void {
    this.paginatorIntl.itemsPerPageLabel = this.translateService.instant('items-per-page');
    this.paginatorIntl.nextPageLabel = this.translateService.instant('next-page');
    this.paginatorIntl.previousPageLabel = this.translateService.instant('previous-page');
    this.paginatorIntl.firstPageLabel = this.translateService.instant('first-page');
    this.paginatorIntl.lastPageLabel = this.translateService.instant('last-page');
    this.paginatorIntl.getRangeLabel = (page: number, pageSize: number, rawLength: number): string => {
      const length = Math.max(rawLength, 0);
      if (length === 0 || pageSize === 0) {
        return this.translateService.instant('paginator-range', { start: 0, end: 0, length });
      }

      const safePage = Math.min(page, Math.max(Math.ceil(length / pageSize) - 1, 0));
      const startIndex = safePage * pageSize;

      return this.translateService.instant('paginator-range', {
        start: startIndex + 1,
        end: Math.min(startIndex + pageSize, length),
        length,
      });
    };
    this.paginatorIntl.changes.next();

    this.datepickerIntl.calendarLabel = this.translateService.instant('calendar');
    this.datepickerIntl.openCalendarLabel = this.translateService.instant('open-calendar');
    this.datepickerIntl.closeCalendarLabel = this.translateService.instant('close-calendar');
    this.datepickerIntl.prevMonthLabel = this.translateService.instant('previous-month');
    this.datepickerIntl.nextMonthLabel = this.translateService.instant('next-month');
    this.datepickerIntl.prevYearLabel = this.translateService.instant('previous-year');
    this.datepickerIntl.nextYearLabel = this.translateService.instant('next-year');
    this.datepickerIntl.prevMultiYearLabel = this.translateService.instant('previous-years');
    this.datepickerIntl.nextMultiYearLabel = this.translateService.instant('next-years');
    this.datepickerIntl.switchToMonthViewLabel = this.translateService.instant('switch-to-month-view');
    this.datepickerIntl.switchToMultiYearViewLabel = this.translateService.instant('switch-to-year-view');
    this.datepickerIntl.changes.next();
  }
}

const supportedLanguages = new Set<string>(Object.values(AvailableLanguagesEnum));

export function supportedLanguage(language: string | null | undefined): AvailableLanguagesEnum | null {
  const baseLanguage = language?.trim().toLowerCase().split(/[-_]/, 1)[0];

  return baseLanguage && supportedLanguages.has(baseLanguage) ? (baseLanguage as AvailableLanguagesEnum) : null;
}

export function languageDirection(language: AvailableLanguagesEnum): 'ltr' | 'rtl' {
  return language === AvailableLanguagesEnum.AR ? 'rtl' : 'ltr';
}
